// The location page's settings: one form, one Save.
//
// It was three cards with three Save buttons — Location details and Service both
// PATCHing /v1/locations/{id}, Posting file PATCHing /v1/streams/{id} — so the
// page could not answer "does this button cover the change I made above it",
// and answered wrong: edit two cards, save one, lose the other silently.
//
// Two tables is not two saves. Create already writes the location and its
// stream in one transaction; the patch endpoint now does the same, so this is
// one request over both halves and there is no half-failure to explain.
//
// The screens list is deliberately NOT in here. It is not settings — it is a
// list with its own create and remove actions, and each of those is its own
// request the moment it is taken.
//
// ServiceConfigCard is deliberately outside the <Form>, for the same reason:
// downloading the posting service's configuration mints a credential through
// its own mutation, and must never dirty this form or wait on its Save.

import { SaveBar } from "@pej/kore/components";
import { ApiError } from "@/api/error";
import { usePatchLocation } from "@/api/locations";
import type { LocationListEntry } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { applyProblemDetails, Form, firstFieldMessage, useKoreForm } from "@/lib/forms";
import { friendlyMessage } from "@/lib/friendly-error";
import { SAVE_BAR_IN_FLOW, useSaveBar } from "@/lib/save-bar";
import { type ConfigureValues, configureSchema, toBody, toDefaults } from "./configure-schema";
import { LocationSection, PostingFileSection } from "./configure-sections";
import { ServiceConfigCard } from "./service-config-card";

export interface LocationConfigureFormProps {
    workspaceId: string;
    location: LocationListEntry;
}

export function LocationConfigureForm({ workspaceId, location }: LocationConfigureFormProps) {
    const patchLocation = usePatchLocation(workspaceId, location.id);
    const form = useKoreForm({ schema: configureSchema, defaultValues: toDefaults(location) });
    const saveBar = useSaveBar({
        dirty: form.formState.isDirty,
        saving: patchLocation.isPending,
        invalid: Object.keys(form.formState.errors).length > 0,
    });

    async function onSubmit(values: ConfigureValues) {
        saveBar.submitting();
        try {
            const updated = await patchLocation.mutateAsync(toBody(values, location.stream.streamId));
            // The response is the same entry shape the list serves, so one body
            // re-seeds every section — including the stream half.
            form.reset(toDefaults(updated));
            saveBar.saved();
        } catch (err) {
            if (err instanceof ApiError) {
                // As on the screen form: violations on their fields, the rest
                // in a toast.
                // The body nests the stream half under `stream`; the form
                // does not.
                const { onFields, unmapped } = applyProblemDetails(form, err, (path) => path.replace(/^stream\./, ""));
                if (onFields) {
                    saveBar.failed(friendlyMessage(err));
                    return;
                }
                toastMutationError("Save failed", err, { unmapped });
                saveBar.failed("Couldn't save this location. Try again.");
                return;
            }
            throw err;
        }
    }

    return (
        <>
            <Form
                form={form}
                onSubmit={onSubmit}
                // `spacing="none"`: kore's Form applies `space-y-6` of its own,
                // which stacked on a flex gap here and doubled every field gap.
                spacing="none"
                className="flex flex-col gap-4"
            >
                <LocationSection control={form.control} />
                <PostingFileSection control={form.control} />
                <SaveBar
                    className={SAVE_BAR_IN_FLOW}
                    state={saveBar.state}
                    errorMessage={saveBar.errorMessage}
                    statusLabels={{ dirty: () => "Unsaved changes" }}
                    onSave={() =>
                        void form.handleSubmit(onSubmit, (errors) => saveBar.blocked(firstFieldMessage(errors)))()
                    }
                    retryLabel="Save changes"
                    onDiscard={() => form.reset(toDefaults(location))}
                />
            </Form>
            <ServiceConfigCard workspaceId={workspaceId} location={location} />
        </>
    );
}
